import { useState } from 'react'
import {
  Box, Button, Chip, CircularProgress, Collapse,
  Divider, IconButton, Tooltip, Typography,
} from '@mui/material'
import {
  Email as EmailIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AttachFile as AttachFileIcon,
  Person as PersonIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { emailApi, EmailLog, EmailEntityType } from '../../lib/api/emailApi'
import { themeColors } from '../../app/layout/theme'

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const STATUS_COLOR: Record<string, { bg: string; text: string }> = {
  SENT: { bg: '#dcfce7', text: '#15803d' },
  DRAFT: { bg: '#fef9c3', text: '#854d0e' },
  FAILED: { bg: '#fee2e2', text: '#dc2626' },
}

// ── Single email row ──────────────────────────────────────────────────────────

function EmailRow({ log }: { log: EmailLog }) {
  const [expanded, setExpanded] = useState(false)
  const sc = STATUS_COLOR[log.status] ?? STATUS_COLOR.SENT

  return (
    <Box sx={{ border: `1px solid ${themeColors.border}`, borderRadius: '8px', overflow: 'hidden' }}>

      {/* Header row */}
      <Box
        onClick={() => setExpanded(v => !v)}
        sx={{
          px: 2, py: 1.5, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 1.5,
          bgcolor: expanded ? '#f9fafb' : '#fff',
          '&:hover': { bgcolor: '#f9fafb' },
        }}
      >
        <EmailIcon sx={{ fontSize: 16, color: themeColors.text.secondary, flexShrink: 0 }} />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontWeight: 600, fontSize: '13px', color: themeColors.text.primary,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
          }}>
            {log.subject}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary, mt: 0.25 }}>
            To: {log.to.join(', ')}
            {log.cc.length > 0 && ` · CC: ${log.cc.join(', ')}`}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {log.attachments.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <AttachFileIcon sx={{ fontSize: 13, color: themeColors.text.secondary, transform: 'rotate(45deg)' }} />
              <Typography sx={{ fontSize: '11px', color: themeColors.text.secondary }}>{log.attachments.length}</Typography>
            </Box>
          )}
          <Chip
            label={log.status}
            size="small"
            sx={{ fontSize: '10px', height: 20, bgcolor: sc.bg, color: sc.text, fontWeight: 700, border: 'none' }}
          />
          <Typography sx={{ fontSize: '11px', color: themeColors.text.secondary, minWidth: 110, textAlign: 'right' }}>
            {formatDate(log.sentAt ?? log.createdAt)}
          </Typography>
          <IconButton size="small" sx={{ p: 0.25, color: themeColors.text.secondary }}>
            {expanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Box>
      </Box>

      {/* Expanded body */}
      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ px: 2.5, py: 2, bgcolor: '#fafafa' }}>

          {/* Meta */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '80px 1fr', rowGap: 0.75, mb: 1.5 }}>
            <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary, fontWeight: 600 }}>From</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PersonIcon sx={{ fontSize: 13, color: themeColors.text.secondary }} />
              <Typography sx={{ fontSize: '12px', color: themeColors.text.primary }}>
                {log.sentByName ?? 'System'}
              </Typography>
            </Box>

            <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary, fontWeight: 600 }}>To</Typography>
            <Typography sx={{ fontSize: '12px', color: themeColors.text.primary }}>{log.to.join(', ')}</Typography>

            {log.cc.length > 0 && (
              <>
                <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary, fontWeight: 600 }}>CC</Typography>
                <Typography sx={{ fontSize: '12px', color: themeColors.text.primary }}>{log.cc.join(', ')}</Typography>
              </>
            )}
            {log.bcc.length > 0 && (
              <>
                <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary, fontWeight: 600 }}>BCC</Typography>
                <Typography sx={{ fontSize: '12px', color: themeColors.text.primary }}>{log.bcc.join(', ')}</Typography>
              </>
            )}
            {log.templateName && (
              <>
                <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary, fontWeight: 600 }}>Template</Typography>
                <Typography sx={{ fontSize: '12px', color: themeColors.text.primary }}>{log.templateName}</Typography>
              </>
            )}
          </Box>

          {/* Body preview */}
          {log.status !== 'FAILED' && (
            <Box
              sx={{
                border: `1px solid ${themeColors.border}`, borderRadius: '6px',
                p: 2, bgcolor: '#fff', fontSize: '13.5px', color: themeColors.text.primary,
                lineHeight: 1.7, maxHeight: 400, overflowY: 'auto',
                '& *': { maxWidth: '100%' },
              }}
              dangerouslySetInnerHTML={{ __html: log.htmlBody || '<em>(No content)</em>' }}
            />
          )}
          {log.status === 'FAILED' && (
            <Box sx={{ py: 1 }}>
              <Typography sx={{ fontSize: '12px', color: '#dc2626', fontStyle: 'italic' }}>
                This email failed to send. No message was delivered.
              </Typography>
            </Box>
          )}

          {/* Attachments */}
          {log.attachments.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: themeColors.text.secondary, mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Attachments
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {log.attachments.map((a, i) => (
                  <Box
                    key={i}
                    onClick={() => a.url && window.open(a.url, '_blank')}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1,
                      border: `1px solid ${themeColors.border}`, borderRadius: '6px',
                      px: 1.5, py: 0.75, bgcolor: '#fff',
                      cursor: a.url ? 'pointer' : 'default',
                      transition: 'all 0.2s',
                      '&:hover': a.url ? { borderColor: '#4f2f86', bgcolor: '#f5f3ff', transform: 'translateY(-1px)', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' } : {},
                    }}
                  >
                    <AttachFileIcon sx={{ fontSize: 14, color: a.url ? '#4f2f86' : '#9ca3af', transform: 'rotate(45deg)' }} />
                    <Typography sx={{
                      fontSize: '12.5px',
                      fontWeight: 500,
                      color: a.url ? '#4f2f86' : '#6b7280',
                      maxWidth: 200,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                    }}>
                      {a.name}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: '#9ca3af' }}>
                      ({formatSize(a.sizeBytes)})
                    </Typography>
                    {a.url ? (
                      <Tooltip title="View / Download">
                        <VisibilityIcon sx={{ fontSize: 14, color: '#4f2f86', ml: 0.5, opacity: 0.8 }} />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Viewing not available for old records">
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#e5e7eb', ml: 0.5 }} />
                      </Tooltip>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  )
}

// ── Section ───────────────────────────────────────────────────────────────────

interface Props {
  entityType: EmailEntityType
  entityId: string
  onCompose: () => void
  canModify: boolean
  hideToolbar?: boolean
}

export function EmailHistorySection({ entityType, entityId, onCompose, canModify, hideToolbar }: Props) {
  const { data: emails = [], isLoading } = useQuery({
    queryKey: ['emails', entityType, entityId],
    queryFn: () => emailApi.getByEntity(entityType, entityId),
  })

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 5 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  return (
    <Box>
      {/* Toolbar */}
      {canModify && !hideToolbar && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<EmailIcon sx={{ fontSize: '13px !important' }} />}
            onClick={onCompose}
            sx={{
              borderRadius: '8px', textTransform: 'none', fontSize: '12px', fontWeight: 600,
              background: '#4f2f86', '&:hover': { background: '#3f236f' },
            }}
          >
            Compose Email
          </Button>
        </Box>
      )}

      {emails.length === 0 ? (
        <Box sx={{ textAlign: 'center', pt: 6 }}>
          <EmailIcon sx={{ fontSize: 44, color: '#d1d5db', mb: 1 }} />
          <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary }}>
            No emails sent yet
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {emails.map(log => <EmailRow key={log.id} log={log} />)}
        </Box>
      )}
    </Box>
  )
}
